import React from 'react';
import { useTranslation } from 'react-i18next';
import type { OnlineUsersComponent } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { ColorPicker } from '../../Common/ColorPicker';
import { ImageUploader } from '../../Common/ImageUploader';
import { ToggleSwitch } from '../../Common/ToggleSwitch';
import { AlignLeft, AlignCenter, AlignRight } from 'lucide-react';

interface OnlineUsersSettingsProps {
  component: OnlineUsersComponent;
}

const STYLE_OPTIONS = ['default', 'compact', 'minimal', 'modern'] as const;

export const OnlineUsersSettings: React.FC<OnlineUsersSettingsProps> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();

  const handleUpdate = (updates: Partial<OnlineUsersComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  return (
    <div className="space-y-6">
      {/* Style Selection */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.onlineUsers.displayStyle')}</label>
        <select
          value={component.settings.style || 'default'}
          onChange={(e) => handleUpdate({ style: e.target.value as any })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {STYLE_OPTIONS.map((key) => (
            <option key={key} value={key}>
              {t(`settings.onlineUsers.styles.${key}` as const)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.onlineUsers.playerCount')}</label>
        <input
          type="number"
          value={component.settings.count}
          onChange={(e) => handleUpdate({ count: parseInt(e.target.value) || 0 })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.title')}</label>
        <input
          type="text"
          value={component.settings.title}
          onChange={(e) => handleUpdate({ title: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <ImageUploader
        label={t('settings.onlineUsers.customIconLabel')}
        value={component.settings.icon}
        onChange={(url) => handleUpdate({ icon: url })}
        aspectRatio="icon"
      />

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.backgroundColor')}</label>
        <ColorPicker
          color={component.settings.backgroundColor}
          onChange={(color) => handleUpdate({ backgroundColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.textColor')}</label>
        <ColorPicker
          color={component.settings.textColor}
          onChange={(color) => handleUpdate({ textColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.iconColor')}</label>
        <ColorPicker
          color={component.settings.iconColor}
          onChange={(color) => handleUpdate({ iconColor: color })}
        />
      </div>

      <div>
        <ToggleSwitch
          label={t('settings.onlineUsers.showPulse')}
          checked={component.settings.showPulse}
          onChange={(checked) => handleUpdate({ showPulse: checked })}
        />
      </div>

      <div>
        <ToggleSwitch
          label={t('settings.onlineUsers.animateCount')}
          checked={component.settings.animateCount}
          onChange={(checked) => handleUpdate({ animateCount: checked })}
        />
        <p className="text-xs text-gray-400 mt-1">
          {t('settings.onlineUsers.animateCountHint')}
        </p>
      </div>

      {component.settings.animateCount && (
        <div>
          <label className="block text-sm font-medium mb-2">{t('settings.onlineUsers.countFluctuation')}</label>
          <input
            type="range"
            min="10"
            max="200"
            step="10"
            value={component.settings.countFluctuation}
            onChange={(e) => handleUpdate({ countFluctuation: parseInt(e.target.value) })}
            className="w-full"
          />
          <div className="text-xs text-gray-400 mt-1">
            {t('settings.onlineUsers.countFluctuationDisplay', { value: component.settings.countFluctuation })}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-3">{t('settings.common.alignment')}</label>
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => handleUpdate({ alignment: 'left' })}
            className={`p-3 rounded-lg border ${
              component.settings.alignment === 'left'
                ? 'bg-white text-gray-900 border-white'
                : 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
            } transition-colors`}
            title={t('settings.common.alignLeft')}
          >
            <AlignLeft size={18} className="mx-auto" />
          </button>
          <button
            onClick={() => handleUpdate({ alignment: 'center' })}
            className={`p-3 rounded-lg border ${
              component.settings.alignment === 'center'
                ? 'bg-white text-gray-900 border-white'
                : 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
            } transition-colors`}
            title={t('settings.common.alignCenter')}
          >
            <AlignCenter size={18} className="mx-auto" />
          </button>
          <button
            onClick={() => handleUpdate({ alignment: 'right' })}
            className={`p-3 rounded-lg border ${
              component.settings.alignment === 'right'
                ? 'bg-white text-gray-900 border-white'
                : 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
            } transition-colors`}
            title={t('settings.common.alignRight')}
          >
            <AlignRight size={18} className="mx-auto" />
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.padding')}</label>
        <input
          type="range"
          min="0"
          max="100"
          value={component.settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
        <div className="text-xs text-gray-400 mt-1">{component.settings.padding}px</div>
      </div>
    </div>
  );
};
